/**
 * MessageList Component
 * Displays list of messages with auto-scroll to bottom
 */

import React, { useRef, useEffect, useCallback } from 'react';
import { Empty, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { Message as MessageComponent } from './Message';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import type { Message } from '@/api/types/chat.types';
import './MessageList.css';

interface MessageListProps {
  /** Array of messages to display */
  messages: Message[];
  /** Chat ID (for optimizations) */
  chatId: string;
  /** Callback to retry failed message */
  onRetryMessage?: (messageId: string) => void;
  /** Callback to load more messages */
  onLoadMore?: () => void;
  /** Callback when scroll position changes */
  onScroll?: (isAtBottom: boolean) => void;
  /** Is loading more messages */
  isLoadingMore?: boolean;
  /** Are there more messages to load */
  hasMoreMessages?: boolean;
}

export const MessageList: React.FC<MessageListProps> = ({
  messages,
  chatId,
  onRetryMessage,
  onLoadMore,
  onScroll,
  isLoadingMore = false,
  hasMoreMessages = false,
}) => {
  const { formatDate } = useUserTimezone();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const prevScrollHeightRef = useRef<number>(0);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  // Check if scrolled to bottom
  const isAtBottom = useCallback(() => {
    const container = containerRef.current;
    if (!container) return true;
    const threshold = 50; // Reduced threshold for more accurate detection
    return (
      container.scrollHeight - container.scrollTop - container.clientHeight <
      threshold
    );
  }, []);

  // Track if user was at bottom before message was added
  const wasAtBottomRef = useRef(true);

  // Handle scroll events
  const handleScroll = useCallback(() => {
    const atBottom = isAtBottom();
    wasAtBottomRef.current = atBottom; // Update ref whenever user scrolls
    if (onScroll) {
      onScroll(atBottom);
    }
  }, [isAtBottom, onScroll]);

  // Set up IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!onLoadMore || !hasMoreMessages) return;

    const options = {
      root: containerRef.current,
      rootMargin: '100px',
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !isLoadingMore) {
          // Save scroll position before loading
          if (containerRef.current) {
            prevScrollHeightRef.current = containerRef.current.scrollHeight;
          }
          onLoadMore();
        }
      });
    }, options);

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [onLoadMore, hasMoreMessages, isLoadingMore]);

  // Preserve scroll position after loading older messages
  useEffect(() => {
    if (
      !isLoadingMore &&
      containerRef.current &&
      prevScrollHeightRef.current > 0
    ) {
      const newScrollHeight = containerRef.current.scrollHeight;
      const scrollDiff = newScrollHeight - prevScrollHeightRef.current;

      if (scrollDiff > 0) {
        containerRef.current.scrollTop += scrollDiff;
        prevScrollHeightRef.current = 0;
      }
    }
  }, [messages, isLoadingMore]);

  // Scroll to bottom when messages change (only if user was at bottom)
  useEffect(() => {
    // Check if user was at bottom before the new message arrived
    if (wasAtBottomRef.current) {
      // Use requestAnimationFrame to wait for DOM to update
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToBottom('smooth');
        });
      });
    }
  }, [messages.length]);

  // Initial scroll to bottom (instant) - after messages load
  useEffect(() => {
    if (messages.length > 0) {
      // Wait for render then scroll
      requestAnimationFrame(() => {
        scrollToBottom('auto');
        wasAtBottomRef.current = true; // Mark as at bottom initially
      });
    }
  }, [chatId, messages.length > 0]);

  // Group messages by date
  const groupMessagesByDate = (messages: Message[]) => {
    const groups: { date: string; messages: Message[] }[] = [];
    let currentDate = '';

    messages.forEach((message) => {
      // Format date in user's timezone (e.g., "17. novembar 2025")
      const messageDate = formatDate(message.created_at, 'D. MMMM YYYY');

      if (messageDate !== currentDate) {
        currentDate = messageDate;
        groups.push({ date: messageDate, messages: [message] });
      } else {
        groups[groups.length - 1].messages.push(message);
      }
    });

    return groups;
  };

  const messageGroups = groupMessagesByDate(messages);

  // Empty state
  if (messages.length === 0) {
    return (
      <div className="message-list message-list-empty">
        <Empty
          description="No messages yet"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div className="message-list" ref={containerRef} onScroll={handleScroll}>
      <div className="message-list-content">
        {/* Loading more indicator */}
        {hasMoreMessages && (
          <div ref={loadMoreRef} className="message-list-load-more">
            {isLoadingMore && (
              <div className="message-list-loading">
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                />
                <span>Loading older messages...</span>
              </div>
            )}
          </div>
        )}
        {messageGroups.map((group, groupIndex) => (
          <div key={groupIndex} className="message-group">
            {/* Date separator */}
            <div className="message-date-separator">
              <span className="message-date-text">{group.date}</span>
            </div>

            {/* Messages for this date */}
            {group.messages.map((message, index) => {
              const prevMessage = index > 0 ? group.messages[index - 1] : null;
              const nextMessage =
                index < group.messages.length - 1
                  ? group.messages[index + 1]
                  : null;

              // Check if we should group this message with previous one
              const sameAuthor = prevMessage?.created_by === message.created_by;
              const timeDiff = prevMessage
                ? new Date(message.created_at).getTime() -
                  new Date(prevMessage.created_at).getTime()
                : Infinity;
              const withinGroupTime = timeDiff < 60000; // 1 minute

              const isGrouped = sameAuthor && withinGroupTime;
              const isLastInGroup =
                !nextMessage ||
                nextMessage.created_by !== message.created_by ||
                new Date(nextMessage.created_at).getTime() -
                  new Date(message.created_at).getTime() >=
                  60000;

              return (
                <MessageComponent
                  key={message.id}
                  message={message}
                  isGrouped={isGrouped}
                  isLastInGroup={isLastInGroup}
                  onRetry={
                    message.status === 'failed' && onRetryMessage
                      ? () => onRetryMessage(message.id)
                      : undefined
                  }
                />
              );
            })}
          </div>
        ))}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};
